import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCart } from "../state/cart";
import { fetchOptionById } from "../api/options";
import { ClickToZoomTailwind } from "./ClickToZoomTailwind";

type SizeItem = { price: number; weight?: number; _id?: string };
type SizeMap = Record<string, SizeItem[]>;

type Props = {
  _id: string;
  title: string;
  price: number;
  imageUrl?: string;
  size?: SizeMap;
  extra?: string[];
  composition?: string;
  taste?: string;
};

export function ProductCard({
  _id,
  title,
  price,
  imageUrl,
  size,
  extra,
  composition,
  taste,
}: Props) {
  const add = useCart((s) => s.add);
  const setQty = useCart((s) => s.setQty);

  const sizes = useMemo(() => {
    if (!size) return [] as { key: string; price: number; weight?: number }[];
    return Object.entries(size)
      .filter(
        ([, arr]) =>
          Array.isArray(arr) && arr.length > 0 && arr[0]?.price != null
      )
      .map(([key, arr]) => ({
        key,
        price: Number(arr[0].price),
        weight: arr[0].weight,
      }));
  }, [size]);

  const [selectedSize, setSelectedSize] = useState<string | null>(
    sizes.length ? null : null
  );

  const { data: tasteOption } = useQuery({
    queryKey: ["tasteOption", taste ?? null],
    queryFn: () => fetchOptionById(taste),
    enabled: !!taste,
  });
  const tastePrice = Number(tasteOption?.price ?? 0);

  const basePrice = useMemo(() => {
    if (sizes.length && selectedSize) {
      const s = sizes.find((x) => x.key === selectedSize);
      if (s) return Number(s.price);
    }
    return Number(price);
  }, [sizes, selectedSize, price]);

  const [tasteOn, setTasteOn] = useState(false);

  const displayPrice = basePrice + (tasteOn ? tastePrice : 0);

  const cartId = useMemo(() => {
    const tasteKey = tasteOn && tasteOption ? `taste:${tasteOption._id}` : "";
    return [_id, selectedSize ?? "", tasteKey].join("::");
  }, [_id, selectedSize, tasteOn, tasteOption]);

  const qty = useCart((s) => s.items.find((i) => i.id === cartId)?.qty ?? 0);

  const hasAnyTasteForThisProduct = useCart((s) =>
    s.items.some((i) => i.id.includes(_id) && i.taste)
  );
  useEffect(() => {
    setTasteOn(Boolean(hasAnyTasteForThisProduct));
  }, [hasAnyTasteForThisProduct]);

  const handleAdd = () => {
    if (sizes.length && !selectedSize) return;

    const tasteMeta =
      tasteOn && tasteOption
        ? {
            id: String(tasteOption._id),
            name: tasteOption.name,
            price: tastePrice,
          }
        : null;

    add(
      {
        id: cartId,
        title,
        price: displayPrice,
        imageUrl,
        sizeKey: selectedSize ?? null,
        taste: tasteMeta,
      },
      1
    );
  };

  const dec = () => setQty(cartId, Math.max(0, qty - 1));
  const inc = () => setQty(cartId, qty + 1);

  const hasImg = !!(imageUrl && imageUrl.trim().length > 0);
  const [showAll, setShowAll] = useState(false);

  return (
    <article className="h-auto border rounded bg-white flex flex-col gap-2 p-2">
      <div className="flex gap-2">
        {hasImg ? (
          <ClickToZoomTailwind imageUrl={imageUrl} title={""} />
        ) : (
          <div className="w-16 h-16 bg-gray-100 rounded grid place-items-center text-[10px] text-gray-400">
            Нет картинки
          </div>
        )}

        <div className="min-w-0">
          <h3 className="text-sm font-semibold line-clamp-2">{title}</h3>
          <div className="text-sm text-gray-600 mt-1">
            {displayPrice.toFixed(0)} руб.
          </div>

          {composition && (
            <div className="mt-1 text-xs text-gray-600">
              <span
                className={showAll ? "" : "line-clamp-2"}
                title={composition}
              >
                {composition}
              </span>
            </div>
          )}
        </div>
      </div>

      {sizes.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-1">
          {sizes.map((s) => {
            const active = selectedSize === s.key;
            return (
              <button
                key={s.key}
                onClick={() => setSelectedSize(s.key)}
                className={`h-8 px-2 rounded border text-xs ${
                  active
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white hover:bg-gray-50"
                }`}
                title={s.weight ? `${s.weight} г` : ""}
              >
                {s.key} {s.weight ? `• ${s.weight}г` : ""} • {s.price} руб.
              </button>
            );
          })}
        </div>
      )}

      {tasteOption && (
        <button
          type="button"
          onClick={() => setTasteOn((v) => !v)}
          className={`mt-1 w-full flex items-center justify-between gap-2 text-xs py-2 px-2 rounded border
            ${
              tasteOn
                ? "bg-yellow-400 border-yellow-400 text-gray-900"
                : "bg-white hover:bg-gray-50"
            }
          `}
          role="switch"
          aria-checked={tasteOn}
        >
          <div className="min-w-0 text-left">
            <div className="truncate">{tasteOption.name}</div>
            {tastePrice > 0 && (
              <div className="text-[11px] opacity-80">
                + {tastePrice.toFixed(1)} руб.
              </div>
            )}
          </div>
          <span
            className={`relative inline-block w-10 h-6 rounded-full border transition
              ${tasteOn ? "bg-yellow-500 border-yellow-500" : "bg-white"}
            `}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform
                ${tasteOn ? "translate-x-4" : ""}
              `}
            />
          </span>
        </button>
      )}

      {Array.isArray(extra) && extra.length > 0 && (
        <div className="text-[11px] text-gray-500">
          Доступны допы: {extra.length} шт.
        </div>
      )}

      <div className="self-end">
        {qty === 0 ? (
          <button
            onClick={handleAdd}
            disabled={sizes.length > 0 && !selectedSize}
            className={`px-2 py-1 text-xs border rounded active:scale-95 ${
              sizes.length > 0 && !selectedSize
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
          >
            В корзину
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={dec} className="w-7 h-7 border rounded">
              −
            </button>
            <span className="min-w-[20px] text-center text-sm">{qty}</span>
            <button onClick={inc} className="w-7 h-7 border rounded">
              +
            </button>
          </div>
        )}
      </div>
    </article>
  );
}
